import { ScrollReveal } from './ScrollReveal';
import { Network, Brain, Database, Plug, ShieldCheck, UserCheck, FileSearch, Lock, Eye } from 'lucide-react';

const LAYERS = [
  { icon: Eye, name: 'Executive Control Layer', desc: 'Full visibility, strategic oversight, and real-time operational intelligence for leadership.' },
  { icon: Network, name: 'Orchestrator Agent', desc: 'Routes and coordinates work across all specialist agents with intelligent prioritization.' },
  { icon: Brain, name: 'Specialist Agents', desc: 'Domain-specific agents perform targeted tasks across departments with structured precision.' },
  { icon: Database, name: 'Memory Layer', desc: 'Preserves business context, institutional knowledge, and historical interaction patterns.' },
  { icon: Plug, name: 'Tool & Integration Layer', desc: 'Connects existing business tools, data sources, and communication platforms.' },
  { icon: ShieldCheck, name: 'Policy Layer', desc: 'Defines permissions, limits, and behavioral boundaries for every agent action.' },
  { icon: UserCheck, name: 'Approval Layer', desc: 'Human-in-the-loop governance for sensitive or high-impact actions requiring authorization.' },
  { icon: FileSearch, name: 'Audit Layer', desc: 'Complete traceability and logging of all agent actions, decisions, and outcomes.' },
  { icon: Lock, name: 'Security Layer', desc: 'Enterprise-grade data protection, access control, and separation by entity and team.' },
];

const ARCH_IMG = 'https://static.prod-images.emergentagent.com/jobs/cf608264-414a-47a4-aa49-755218a60bb9/images/0ef433816ccd9716171c3d594c904e33d39e6cc3adb82d0f67195b3c683a4478.png';

export default function HowItWorks() {
  return (
    <section id="architecture" className="relative py-24 sm:py-32 overflow-hidden" data-testid="how-it-works-section">
      <div className="absolute inset-0">
        <img src={ARCH_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" loading="lazy" />
        <div className="absolute inset-0 bg-aurocle-base/90" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="overline mb-4">System Architecture</div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">
            How it works.
          </h2>
          <p className="text-base text-aurocle-platinum-muted max-w-2xl mb-16">
            A layered architecture designed for control, scalability, and enterprise-grade reliability. Every action is governed, every outcome is traceable.
          </p>
        </ScrollReveal>

        <div className="relative">
          <div className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-aurocle-gold/30 via-aurocle-gold/10 to-transparent hidden sm:block" />

          <div className="space-y-6">
            {LAYERS.map((layer, i) => {
              const Icon = layer.icon;
              const isRight = i % 2 === 1;
              return (
                <ScrollReveal key={i} delay={i * 80}>
                  <div className={`relative flex items-start gap-6 ${isRight ? 'lg:flex-row-reverse' : ''}`}>
                    <div className={`flex-1 ${isRight ? 'lg:text-right' : ''}`}>
                      <div className={`glass-card p-6 inline-block max-w-lg ${isRight ? 'lg:ml-auto' : ''}`}>
                        <div className={`flex items-center gap-3 mb-2 ${isRight ? 'lg:flex-row-reverse' : ''}`}>
                          <div className="w-8 h-8 flex items-center justify-center border border-aurocle-gold/20 shrink-0">
                            <Icon size={14} className="text-aurocle-gold" />
                          </div>
                          <span className="font-mono text-xs text-aurocle-gold/60 uppercase tracking-wider">0{i + 1}</span>
                          <h3 className="font-heading text-sm font-medium text-aurocle-text">{layer.name}</h3>
                        </div>
                        <p className="text-xs text-aurocle-platinum-muted leading-relaxed">{layer.desc}</p>
                      </div>
                    </div>
                    <div className="hidden lg:block flex-1" />
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
